// Odd Coin: find the fake coin with as few weighings on a balance scale as you can.
//
// The scale is honest, but the fake coin is not fixed in advance: the scale always answers in the way that is
// worst for you among the answers that are still possible ("the gremlin picks the coin after seeing your plan").
// So luck cannot solve it; only a plan that works whatever happens can.
//
// What we know about each coin is a two-bit set: bit 0 = "could be the fake and heavy", bit 1 = "could be the
// fake and light". Coins with no bits are known to be genuine.

import type { Rng } from "../../../shared/rng";

export type Mode = "heavy" | "either" | "coin";

export interface CoinsSpec {
  n: number;
  weighings: number;
  /** heavy: the fake is heavier. either: heavier or lighter, and you must say which. coin: heavier or lighter, and you only name the coin. */
  mode: Mode;
  title: string;
}

export type Outcome = "=" | "L" | "R"; // L: the left pan sank (is heavier)

export interface Weighing {
  l: number[];
  r: number[];
}

export interface Replayed {
  poss: Uint8Array;
  outcomes: Outcome[];
}

export function initialPoss(spec: CoinsSpec): Uint8Array {
  return new Uint8Array(spec.n).fill(spec.mode === "heavy" ? 1 : 3);
}

/** the knowledge after an outcome; a coin keeps only the possibilities that explain what the scale did */
function apply(poss: Uint8Array, w: Weighing, out: Outcome): Uint8Array {
  const next = new Uint8Array(poss.length);
  const onL = new Set(w.l);
  const onR = new Set(w.r);
  for (let i = 0; i < poss.length; i++) {
    const p = poss[i];
    if (out === "=") next[i] = onL.has(i) || onR.has(i) ? 0 : p;
    else if (out === "L") next[i] = onL.has(i) ? p & 1 : onR.has(i) ? p & 2 : 0;
    else next[i] = onL.has(i) ? p & 2 : onR.has(i) ? p & 1 : 0;
  }
  return next;
}

const anyPossible = (poss: Uint8Array): boolean => poss.some((p) => p !== 0);

export interface Kind {
  E: number; // could be heavy or light
  H: number; // could only be heavy
  L: number; // could only be light
  G: number; // genuine
}

export function kindOf(poss: Uint8Array): Kind {
  const k = { E: 0, H: 0, L: 0, G: 0 };
  for (const p of poss) {
    if (p === 3) k.E++;
    else if (p === 1) k.H++;
    else if (p === 2) k.L++;
    else k.G++;
  }
  return k;
}

/** how many (coin, direction) stories are still possible */
export const stories = (k: Kind): number => 2 * k.E + k.H + k.L;

export function isResolved(k: Kind, mode: Mode): boolean {
  return mode === "coin" ? k.E + k.H + k.L <= 1 : stories(k) <= 1;
}

// ---- the search: can the player still be sure to win, and what is a good weighing? ----

interface Counts {
  eL: number; eR: number; hL: number; hR: number; lL: number; lR: number; gL: number; gR: number;
}

/** knowledge after the scale shows `out` for a weighing described by how many coins of each kind sit on each pan */
function nextKind(k: Kind, c: Counts, out: Outcome): Kind | null {
  if (out === "=") {
    const E = k.E - c.eL - c.eR;
    const H = k.H - c.hL - c.hR;
    const L = k.L - c.lL - c.lR;
    if (E + H + L === 0) return null;
    return { E, H, L, G: k.E + k.H + k.L + k.G - E - H - L };
  }
  const H = out === "L" ? c.eL + c.hL : c.eR + c.hR;
  const L = out === "L" ? c.eR + c.lR : c.eL + c.lL;
  if (H + L === 0) return null;
  return { E: 0, H, L, G: k.E + k.H + k.L + k.G - H - L };
}

const memo = new Map<string, boolean>();

export function canWin(k: Kind, r: number, mode: Mode): boolean {
  if (isResolved(k, mode)) return true;
  if (r <= 0) return false;
  // every outcome sequence can name only one coin; when direction doesn't matter, the one coin that is never
  // weighed may keep both of its stories inside a single sequence
  const needed = mode === "coin" && k.E > 0 ? stories(k) - 1 : stories(k);
  if (needed > Math.pow(3, r)) return false;
  const key = `${k.E},${k.H},${k.L},${k.G},${r},${mode}`;
  const hit = memo.get(key);
  if (hit !== undefined) return hit;
  const found = search(k, r, mode) !== null;
  memo.set(key, found);
  return found;
}

/** the first weighing (most coins on the scale first) after which every possible answer is still winnable */
function search(k: Kind, r: number, mode: Mode): Counts | null {
  const total = k.E + k.H + k.L + k.G;
  for (let t = Math.floor(total / 2); t >= 1; t--) {
    for (let eL = 0; eL <= Math.min(k.E, t); eL++)
      for (let eR = 0; eR <= Math.min(k.E - eL, t); eR++)
        for (let hL = 0; hL <= Math.min(k.H, t - eL); hL++)
          for (let hR = 0; hR <= Math.min(k.H - hL, t - eR); hR++)
            for (let lL = 0; lL <= Math.min(k.L, t - eL - hL); lL++)
              for (let lR = 0; lR <= Math.min(k.L - lL, t - eR - hR); lR++) {
                const gL = t - eL - hL - lL;
                const gR = t - eR - hR - lR;
                if (gL < 0 || gR < 0 || gL + gR > k.G) continue;
                const c: Counts = { eL, eR, hL, hR, lL, lR, gL, gR };
                let ok = true;
                for (const out of ["=", "L", "R"] as const) {
                  const nk = nextKind(k, c, out);
                  if (nk && !canWin(nk, r - 1, mode)) {
                    ok = false;
                    break;
                  }
                }
                if (ok) return c;
              }
  }
  return null;
}

/** pick actual coins for a counts-description: lowest numbers first within each kind */
function realise(poss: Uint8Array, c: Counts): Weighing {
  const byKind: Record<"E" | "H" | "L" | "G", number[]> = { E: [], H: [], L: [], G: [] };
  poss.forEach((p, i) => byKind[p === 3 ? "E" : p === 1 ? "H" : p === 2 ? "L" : "G"].push(i));
  const take = (list: number[], n: number) => list.splice(0, n);
  const l = [...take(byKind.E, c.eL), ...take(byKind.H, c.hL), ...take(byKind.L, c.lL), ...take(byKind.G, c.gL)];
  const r = [...take(byKind.E, c.eR), ...take(byKind.H, c.hR), ...take(byKind.L, c.lR), ...take(byKind.G, c.gR)];
  return { l: l.sort((a, b) => a - b), r: r.sort((a, b) => a - b) };
}

/** A weighing that keeps the player's win guaranteed, or null when no such weighing exists any more. */
export function suggestWeighing(spec: CoinsSpec, poss: Uint8Array, used: number): Weighing | null {
  const k = kindOf(poss);
  const r = spec.weighings - used;
  if (isResolved(k, spec.mode) || r <= 0) return null;
  const c = search(k, r, spec.mode);
  return c ? realise(poss, c) : null;
}

// ---- playing ----

/**
 * The scale's answer. Among the answers that are still possible it prefers one that leaves the player
 * unable to win within the weighings left; otherwise the answer that keeps the most suspects.
 */
export function scaleAnswer(spec: CoinsSpec, poss: Uint8Array, w: Weighing, used: number): Outcome {
  const left = spec.weighings - used - 1;
  const options: { out: Outcome; poss: Uint8Array; size: number; win: boolean }[] = [];
  for (const out of ["=", "L", "R"] as const) {
    const next = apply(poss, w, out);
    if (!anyPossible(next)) continue;
    const k = kindOf(next);
    options.push({ out, poss: next, size: stories(k), win: canWin(k, left, spec.mode) });
  }
  options.sort((a, b) => Number(a.win) - Number(b.win) || b.size - a.size);
  return options[0].out;
}

/** replay a list of weighings from scratch; the scale's answers depend only on the plan, never on chance */
export function replay(spec: CoinsSpec, ws: readonly Weighing[]): Replayed {
  let poss = initialPoss(spec);
  const outcomes: Outcome[] = [];
  ws.forEach((w, i) => {
    const out = scaleAnswer(spec, poss, w, i);
    outcomes.push(out);
    poss = apply(poss, w, out);
  });
  return { poss, outcomes };
}

export type Phase = "playing" | "solved" | "stuck" | "doomed";

export function phaseOf(spec: CoinsSpec, poss: Uint8Array, used: number): Phase {
  const k = kindOf(poss);
  if (isResolved(k, spec.mode)) return "solved";
  if (used >= spec.weighings) return "stuck";
  return canWin(k, spec.weighings - used, spec.mode) ? "playing" : "doomed";
}

/** the fake, once known */
export function verdict(poss: Uint8Array): { coin: number; heavier: boolean | null } | null {
  const idx: number[] = [];
  poss.forEach((p, i) => p && idx.push(i));
  if (idx.length !== 1) return null;
  const p = poss[idx[0]];
  return { coin: idx[0], heavier: p === 1 ? true : p === 2 ? false : null };
}

export function validWeighing(spec: CoinsSpec, w: Weighing): boolean {
  if (!w.l.length || w.l.length !== w.r.length) return false;
  const seen = new Set<number>();
  for (const i of [...w.l, ...w.r]) {
    if (!Number.isInteger(i) || i < 0 || i >= spec.n || seen.has(i)) return false;
    seen.add(i);
  }
  return true;
}

// ---- puzzle table ----

export function makeSpec(n: number, weighings: number, mode: Mode): CoinsSpec {
  const goal =
    mode === "heavy"
      ? `One of these ${n} coins is heavier than the others. Find it using the scale just ${weighings} times.`
      : mode === "either"
        ? `One of these ${n} coins is a fake: a little heavier or a little lighter. Find it, and say which, in ${weighings} weighings.`
        : `One of these ${n} coins is a fake: a little heavier or a little lighter. Name it in ${weighings} weighings. You don't need to say which.`;
  return { n, weighings, mode, title: goal };
}

export function generateCoins(rng: Rng, difficulty: number): CoinsSpec {
  switch (Math.max(1, Math.min(4, difficulty))) {
    case 1: return makeSpec(rng.int(7, 9), 2, "heavy");
    case 2: return makeSpec(rng.int(19, 27), 3, "heavy");
    case 3: return makeSpec(rng.int(10, 12), 3, "either");
    default: return makeSpec(13, 3, "coin");
  }
}
